import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Divider, Grid } from '@material-ui/core'
import * as Yup from 'yup'
import Form from 'shared/components/Form'
import Input from 'shared/components/Input'
import { colors } from 'shared/styles/colors'
import Footer from '../components/Footer'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import { useOnboarding } from 'modules/pagamentos/hooks/useOnboarding'
import getValidationErrors, {
  IValidationErrors,
} from 'shared/utils/getValidationErrors'
import { useFrotista } from 'modules/user/hooks/useFrotista'
import SnackBar from 'shared/components/SnackBar'
import { IAceitarTermosOnboarding } from 'modules/pagamentos/domain/usecases/AceitarTermosOnboarding'
import { IEnviarDocumentoOnboarding } from 'modules/pagamentos/domain/usecases/EnviarDocumentoOnboarding'
import { ICreateContaPJOnboarding } from 'modules/pagamentos/domain/usecases/CreateContaPJOnboarding'
import { IConfirmarEnvioDocumentosOnboarding } from 'modules/pagamentos/domain/usecases/ConfirmarEnvioDocumentosOnboarding'
import { IValidarPinTmovPay } from 'modules/pagamentos/domain/usecases/ValidarPinTmovPay'
import { useAuth } from 'modules/auth/hooks/auth'
import { phoneMask } from 'shared/utils/formatters'
import EModoEnvioPin from 'modules/pagamentos/domain/entities/EModoEnvioPin'
import { IEnviarPinTmovPay } from 'modules/pagamentos/domain/usecases/EnviarPinTmovPay'
import Text from 'shared/components/Text'
import { Subtitle, Title } from '../styles'
import { ContainerButton, ResendButton } from './styles'
import { EOrigemSolicitacaoPin } from 'modules/pagamentos/domain/entities/EOrigemEnvioPin'

interface ValidacaoProps {
  aceitarTermosDeUsoOnboarding: IAceitarTermosOnboarding
  enviarDocumentoOnboarding: IEnviarDocumentoOnboarding
  createContaPJOnboarding: ICreateContaPJOnboarding
  confirmarEnvioDocumentosOnboarding: IConfirmarEnvioDocumentosOnboarding
  enviarPinTmovPay: IEnviarPinTmovPay
  validarPinTmovPay: IValidarPinTmovPay
}

interface IFormProps {
  codigo: string
}

const Validacao: FunctionComponent<ValidacaoProps> = ({
  aceitarTermosDeUsoOnboarding,
  enviarDocumentoOnboarding,
  createContaPJOnboarding,
  confirmarEnvioDocumentosOnboarding,
  enviarPinTmovPay,
  validarPinTmovPay,
}) => {
  const {
    handleBackButton,
    handleNextButton,
    contaPj,
    documentos,
    removeDocumentosEmpresa,
    setSuccess,
  } = useOnboarding()
  const { frotistaId } = useFrotista()
  const { telefone } = useAuth()
  const [loading, setLoading] = useState(false)
  const [loadingResend, setLoadingResend] = useState(false)
  const [isOk, setIsOk] = useState(false)
  const code = useRef('')
  const [codigo, setCodigo] = useState('')
  const [counter, setCounter] = useState(180)
  const [errorMessageForm, setErrorMessageForm] = useState({} as IFormProps)
  const [openErrorMessage, setOpenErrorMessage] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const semErrosDeValidacao = Object.values(errorMessageForm).every(
    errorMessage => errorMessage === '',
  )
  const [token, setToken] = useState('')
  const [tipoEnvio, setTipoEnvio] = useState(EModoEnvioPin.SMS)

  useEffect(() => {
    const timer =
      counter > 0 && setInterval(() => setCounter(counter - 1), 1000)
    return () => clearInterval(timer)
  }, [counter])

  useEffect(() => {
    const enviarPin = async () => {
      setLoading(true)
      try {
        const response = await enviarPinTmovPay.execute({
          contato: telefone,
          tipoEnvio: EModoEnvioPin.SMS,
          origem: EOrigemSolicitacaoPin.FROTISTA_TMOVPAY_ONBOARDING,
        })

        setToken(response.token)
        setTipoEnvio(response.tipoReenvio)
        setLoadingResend(false)
      } catch (e: unknown) {
        if (e instanceof Error) {
          setErrorMessage(e.message)
        } else {
          setErrorMessage('Erro ao tentar enviar o pin')
        }
      } finally {
        setLoading(false)
      }
    }
    enviarPin()
  }, [])

  const handleResendCode = useCallback(async () => {
    setErrorMessage('')
    setLoadingResend(true)
    setCounter(180)

    try {
      await enviarPinTmovPay.execute({
        contato: telefone,
        tipoEnvio,
        origem: EOrigemSolicitacaoPin.FROTISTA_TMOVPAY_ONBOARDING,
      })

      setLoadingResend(false)
    } catch (e: unknown) {
      if (e instanceof Error) {
        setErrorMessageForm({
          codigo: e.message,
        })
        setErrorMessage(e.message)
      } else {
        setErrorMessage('Erro ao tentar enviar o pin')
      }
      setLoadingResend(false)
    }
  }, [enviarPinTmovPay])

  const showValidationErrors = (errors: IValidationErrors) => {
    setErrorMessageForm({
      codigo: errors.codigo,
    })
  }

  const handleValidarPin = async () => {
    setLoading(true)
    setErrorMessage('')
    try {
      if (!code) {
        setErrorMessage('Informe o código')
        setLoading(false)

        return
      }

      await validarPinTmovPay.execute({
        pin: code.current,
        token,
        celular: telefone,
      })
      setIsOk(true)
    } catch (error: any) {
      setIsOk(false)
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error)
        showValidationErrors(errors)
      } else if (error instanceof Error) {
        setErrorMessageForm({
          codigo: 'Código incorreto. Tente novamente.',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleOnBlur = async (
    field: string,
    value: string,
    message?: string,
  ): Promise<void> => {
    if (!value) {
      setErrorMessageForm(errors => ({
        ...errors,
        [field]: message || 'Campo obrigatório',
      }))
    } else {
      setErrorMessageForm(errors => ({
        ...errors,
        [field]: '',
      }))
    }

    if (semErrosDeValidacao) setIsOk(true)
    else setIsOk(false)

    if (value.length === 6) {
      await handleValidarPin()
    }
  }

  const handleSubmit = async () => {
    try {
      setErrorMessage('')
      setOpenErrorMessage(false)

      const schema = Yup.object().shape({
        codigo: Yup.string()
          .min(6, 'Digite um código com 6 dígitos.')
          .max(6, 'Digite um código com 6 dígitos.')
          .required('Digite um código com 6 dígitos.'),
      })
      await schema.validate(
        {
          codigo,
        },
        {
          abortEarly: false,
        },
      )
      await handleValidarPin()

      setLoading(true)

      delete contaPj.responsaveis.pessoas_fisicas[0].cidade
      delete contaPj.responsaveis.pessoas_fisicas[0].estado

      await createContaPJOnboarding.execute({
        ...contaPj,
        frotista_id: frotistaId,
      })
      await Promise.all(
        documentos.map(
          async documento =>
            await enviarDocumentoOnboarding.execute({
              arquivo: documento.arquivo,
              frotista_id: frotistaId,
              tipo: documento.tipo,
            }),
        ),
      )
      await confirmarEnvioDocumentosOnboarding.execute({
        documentacao_enviada: true,
        frotista_id: frotistaId,
      })
      setSuccess(true)
    } catch (error: any) {
      setSuccess(false)
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error)
        setErrorMessage(errors.message)
      }
      setErrorMessage(error.message)
      setOpenErrorMessage(true)
    } finally {
      setLoading(false)
    }
  }

  const handleGoBack = () => {
    handleBackButton()
  }

  return (
    <>
      {loading && <LoadingBackdrop open={loading} color={colors.green} />}
      <Divider style={{ margin: '32px 0' }} />
      <Grid container>
        <Grid item xs={12} sm={12} md={3} lg={3}>
          <Title>Verificação de celular</Title>
          <Subtitle>
            Digite o código de 6 dígitos enviado por SMS para{' '}
            {phoneMask(telefone)}.
          </Subtitle>
        </Grid>
        <Form
          onSubmit={undefined}
          initialValues={{
            codigo: '',
          }}
          validationSchema={() => ({
            codigo: Yup.string()
              .min(6, 'Digite um código com 6 dígitos.')
              .max(6, 'Digite um código com 6 dígitos.')
              .required('Digite um código com 6 dígitos.'),
          })}
        >
          {formControl => (
            <Grid container>
              <Grid container spacing={2} style={{ margin: '8px 0' }}>
                <Grid item xs={12} sm={12} md={4} lg={4}>
                  <Input
                    placeholder="Informe o código"
                    label="Código"
                    name="codigo"
                    type="text"
                    maxLength={6}
                    formControl={formControl}
                    onBlur={() =>
                      handleOnBlur(
                        'codigo',
                        formControl.values.codigo,
                        'Digite um código com 6 dígitos.',
                      )
                    }
                    errorMessage={errorMessageForm.codigo}
                    onChange={e => {
                      setCodigo(e.target.value)
                      code.current = e.target.value
                    }}
                  />
                  <ContainerButton>
                    <Text size={14} weight="400" color={colors.gray7}>
                      Não recebeu o código?
                    </Text>

                    <ResendButton
                      transparent={true}
                      color={colors.grayLight}
                      disabled={counter > 0 || loadingResend}
                      loading={loadingResend}
                      type="button"
                      onClick={handleResendCode}
                    >
                      Reenviar via Whatsapp
                    </ResendButton>
                    <Text size={14} weight="400" color={colors.gray7}>
                      {`(${Math.floor(counter / 60)}:${Math.floor(counter % 60)
                        .toString()
                        .padStart(2, '0')})`}
                    </Text>
                  </ContainerButton>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Form>
      </Grid>
      <Divider style={{ margin: '32px 0' }} />

      <Grid container>
        <Footer
          onClickBackButton={handleGoBack}
          onClickNextButton={handleSubmit}
          titleNextButton="Concluir"
          titleBackButton="Voltar"
          isNextButtonDisabled={!isOk}
        />
      </Grid>
      <SnackBar
        open={openErrorMessage}
        setOpen={setOpenErrorMessage}
        severity="error"
        message={errorMessage}
        horizontal="right"
      />
    </>
  )
}

export default Validacao
